/* IntegrationsComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./IntegrationsRenderer");
const _ = require("lodash");

class IntegrationsComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        VendorApiConfig,
        uiUtils,
        steve,
        logger,
        WorkflowTaskType,
        modalUserFeedback,
        CredentialsEntityDef,
        VersionControlConfigDef,
        ServiceConnectionEntityDef,
        GitService,
        Router
    ) {
        super();
        this._userSession = userSession;
        this._vendorApiConfigEntity = VendorApiConfig;
        this._ciscoServiceConnectionEntities = [];
        this._vendorApiConfig = undefined;
        this._versionControlConfigEntityDef = VersionControlConfigDef;
        this._versionControlConfigEntity = undefined;
        this._serviceConnectionEntityDef = ServiceConnectionEntityDef;
        this._serviceConnectionEntities = [];
        this._gitService = GitService;
        this._uiUtils = uiUtils;
        this._router = Router;
        this._steve = steve;
        this._logger = logger;
        this._WorkflowTaskTypeEntity = WorkflowTaskType;
        this._modalUserFeedback = modalUserFeedback;
        this._credentialsEntityDef = CredentialsEntityDef;
        this._credentialsList = [];
        this._onVersionControlConfigStatusPropertyUpdated =
            this._onVersionControlConfigStatusPropertyUpdated.bind(this);
        this._renderer = new Renderer(this, this._uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._versionControlConfigEntity.on(
            "StatusPropertyUpdated",
            this._onVersionControlConfigStatusPropertyUpdated
        );
    }

    _unregisterListeners() {
        this._versionControlConfigEntity.removeListener(
            "StatusPropertyUpdated",
            this._onVersionControlConfigStatusPropertyUpdated
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        const promArr = [this._renderer._shutdown(), this._versionControlConfigEntity.shutdown()];
        return Promise.all(promArr).then(
            () => {
                this._userSession = undefined;
                this._vendorApiConfigEntity = undefined;
                this._vendorApiConfig = undefined;
                this._uiUtils = undefined;
                this._steve = undefined;
                this._logger = undefined;
                this._renderer = undefined;
                this._ciscoConnectionOptions = undefined;
                this._ciscoServiceConnectionEntities = undefined;
                this._versionControlConfigEntityDef = undefined;
                this._versionControlConfigEntity = undefined;
                this._serviceConnectionEntityDef = undefined;
                this._serviceConnectionEntities = undefined;
            },
            (err) => {
                console.log("Error shutting down IntegrationsComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    set vendorApiConfig(newValue) {
        this._vendorApiConfig = newValue;
    }

    get vendorApiConfig() {
        return this._vendorApiConfig;
    }

    set vendorApiConfigEntity(newValue) {
        this._vendorApiConfigEntity = newValue;
    }

    get vendorApiConfigEntity() {
        return this._vendorApiConfigEntity;
    }

    get modalUserFeedback() {
        return this._modalUserFeedback;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        const promArr = [
            this._vendorApiConfigEntity.retrieveVendorApiConfig(this.activeOrgId),
            this._credentialsEntityDef.loadLabelValues(this.activeOrgId),
            this.getCiscoServiceConnectionEntities(),
            this._loadVersionControlEntity(),
            this._loadServiceConnections(),
        ];
        return Promise.all(promArr)
            .then((results) => {
                this._vendorApiConfig = results[0];
                this._credentialsList = results[1] || [];
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "IntegrationsComponent.whenModelIsReady() error.");
                return Promise.reject(err);
            })
            .finally(() => {
                this._registerListeners();
            });
    }

    organizationChanged() {
        this.emit("IntegrationsOrganizationChanged");
    }

    reloadData() {
        return Promise.all([
            this.resetVendorApiConfigData(),
            this.getCiscoServiceConnectionEntities(),
            this.resetAllCredentialsData(),
            this._loadVersionControlEntity(),
            this._loadServiceConnections(),
        ]).catch((err) => {
            this._logger.warn(err, "IntegrationsComponent.reloadData() error.");
        });
    }

    resetVendorApiConfigData() {
        return this._vendorApiConfigEntity
            .retrieveVendorApiConfig(this.activeOrgId)
            .then((vendorApiConfig) => {
                this._vendorApiConfig = vendorApiConfig;
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "IntegrationsComponent.resetApiConfigData() error.");
                return Promise.reject(err);
            });
    }

    getCiscoServiceConnectionEntities() {
        const query = {serviceConnectionTypeName: "svcConnection/ciscoApi.svcConnection"};
        return this._serviceConnectionEntityDef
            .loadAscendServiceConnections(this.activeOrgId, query)
            .then((entites) => {
                this._ciscoServiceConnectionEntities = entites || [];
                this._ciscoConnectionOptions = entites.map((entity) => {
                    return {
                        label: entity.name,
                        value: entity.id,
                    };
                });
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "IntegrationsComponent.getServiceConnections() error.");
                return Promise.reject(err);
            });
    }

    resetVersionConfigData() {
        this._versionControlConfigEntity.clearPendingPatches();
        return Promise.resolve();
    }

    resetAllCredentialsData() {
        return this._credentialsEntityDef
            .loadLabelValues(this.activeOrgId)
            .then((credentials) => {
                this._credentialsList = credentials || [];
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "IntegrationsComponent.resetAllCredentialsData() error.");
                return Promise.reject(err);
            });
    }

    async _loadVersionControlEntity() {
        const versionControlConfigEntityArr =
            await this._versionControlConfigEntityDef.loadVersionControlConfigs(this.activeOrgId);
        if (versionControlConfigEntityArr.length > 0) {
            const vcConfigId = versionControlConfigEntityArr[0].getId();
            this._versionControlConfigEntity =
                await this._versionControlConfigEntityDef.retrieveVersionControlConfig(
                    this.activeOrgId,
                    vcConfigId
                );
        } else {
            const versionControlConfigEntity = new this._versionControlConfigEntityDef(
                this.activeOrgId
            );
            await versionControlConfigEntity.create();
            this._versionControlConfigEntity =
                await this._versionControlConfigEntityDef.retrieveVersionControlConfig(
                    this.activeOrgId,
                    versionControlConfigEntity.id
                );
        }

        return Promise.resolve();
    }

    async _loadServiceConnections() {
        this._serviceConnectionEntities =
            (await this._serviceConnectionEntityDef.loadAscendServiceConnections(this.activeOrgId, {
                $and: [
                    {serviceConnectionTypeName: "svcConnection/git.svcConnection"},
                    {
                        $or: [
                            {"connectionInformation.isLabRepo": {$exists: false}},
                            {"connectionInformation.isLabRepo": {$ne: true}},
                        ],
                    },
                ],
            })) || [];

        if (this._serviceConnectionEntities) {
            this._connectionDropdownEntries = this._serviceConnectionEntities.reduce((acc, val) => {
                acc.push({
                    label: val.getName(),
                    value: val.getId(),
                });
                return acc;
            }, []);
            return this._serviceConnectionEntities;
        }
        return [];
    }

    _onVersionControlConfigStatusPropertyUpdated(value) {
        this.emit("VersionControlConfigStatusPropertyUpdated", value);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    async testGitConnectivity(connectionId) {
        try {
            // Active org, not the connection's owning org: an inherited connection's tenant is an
            // ancestor the caller may not be authorized to launch work against.
            const workTarget = {
                category: "entity",
                entityType: "ServiceConnection",
                method: "single",
                id: connectionId,
            };
            const response = await this._serviceConnectionEntityDef.runTestConnectionWork(
                this.activeOrgId,
                workTarget
            );
            if (response.summary.connected) {
                return Promise.resolve();
            } else {
                return Promise.reject({errMsg: "Connection to Git Failed"});
            }
        } catch (err) {
            return Promise.reject({errMsg: "Connection to Git Failed"});
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = IntegrationsComponent;

IntegrationsComponent.$groupName = "Organization";

IntegrationsComponent.$displayName = "Integrations";

IntegrationsComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.entities.VendorApiConfig",
    "$teve.ui.UiUtils",
    "$teve.core",
    "$teve.logger",
    "$teve.entities.WorkflowTaskType",
    "$teve.ui.userFeedback",
    "$teve.entities.Credentials",
    "$teve.entities.VersionControlConfig",
    "$teve.entities.ServiceConnection",
    "$teve.services.Git",
    "$teve.router",
];
